/**
 * Derive simulated-balance history and resolved pick stats for Tracked Picks.
 */

/**
 * @typedef {{ date: number, balanceDollars: number }} BalancePoint
 */

/**
 * Build time-ordered points for a balance-over-time chart by replaying stake
 * deductions at ``created_at`` and win payouts at ``resolved_at`` (matches
 * backend: loss does not refund stake; win returns stake + profit).
 *
 * Starting balance is inferred so the series ends at ``currentBalanceCents``.
 *
 * @param {Array<Record<string, unknown>>} picks - Full watched pick list from API
 * @param {number | null | undefined} currentBalanceCents - User's current balance in cents
 * @returns {{ points: BalancePoint[], hasWagerHistory: boolean }}
 */
export function buildBalanceHistoryPoints(picks, currentBalanceCents) {
  const current =
    currentBalanceCents != null && Number.isFinite(Number(currentBalanceCents))
      ? Number(currentBalanceCents)
      : null;
  if (current == null) {
    return { points: [], hasWagerHistory: false };
  }

  /** @type {{ t: number, delta: number }[]} */
  const events = [];
  for (const p of picks) {
    const stake = Number(p.stake_cents) || 0;
    if (stake <= 0) continue;
    const created = p.created_at ? Date.parse(String(p.created_at)) : NaN;
    if (!Number.isNaN(created)) {
      events.push({ t: created, delta: -stake });
    }
    if (p.outcome === 'won' && p.resolved_at) {
      const resolved = Date.parse(String(p.resolved_at));
      if (!Number.isNaN(resolved)) {
        const profitCents = Math.round((Number(p.profit_dollars) || 0) * 100);
        events.push({ t: resolved, delta: stake + profitCents });
      }
    }
  }

  events.sort((a, b) => a.t - b.t || 0);

  if (events.length === 0) {
    const now = Date.now();
    return {
      points: [
        { date: now - 86400000, balanceDollars: current / 100 },
        { date: now, balanceDollars: current / 100 },
      ],
      hasWagerHistory: false,
    };
  }

  const totalDelta = events.reduce((s, e) => s + e.delta, 0);
  let balanceCents = current - totalDelta;

  const padMs = 3600000; // 1h before first event so the line enters from the left
  const t0 = events[0].t - padMs;

  /** @type {BalancePoint[]} */
  const points = [{ date: t0, balanceDollars: balanceCents / 100 }];
  for (const e of events) {
    balanceCents += e.delta;
    points.push({ date: e.t, balanceDollars: balanceCents / 100 });
  }

  return { points, hasWagerHistory: true };
}

/**
 * Wins, losses, and win rate among resolved picks (won + lost only).
 *
 * @param {Array<Record<string, unknown>>} picks
 * @returns {{ wins: number, losses: number, resolved: number, winPct: number | null, recordLabel: string }}
 */
export function computeResolvedRecord(picks) {
  let wins = 0;
  let losses = 0;
  for (const p of picks) {
    if (p.outcome === 'won') wins += 1;
    else if (p.outcome === 'lost') losses += 1;
  }
  const resolved = wins + losses;
  const winPct =
    resolved > 0 ? Math.round((wins / resolved) * 1000) / 10 : null;
  const recordLabel = resolved > 0 ? `${wins}-${losses}` : '—';
  return { wins, losses, resolved, winPct, recordLabel };
}
